import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut, apiPost } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  Bell, Mail, Plus, X, Send, Save, Loader2, AlertTriangle, Clock,
  Power, ShieldOff,
} from 'lucide-react';

// Group label → matcher fn against event_type. Order = display order.
const CATEGORIES = [
  { key: 'hr', label: 'HR & People',
    match: (k) => /(employee|leave|attendance|regularization|onboarding|welcome|training|payroll|comp_off|punch)/i.test(k) },
  { key: 'finance', label: 'Finance',
    match: (k) => /(payment|invoice|receipt|tds|gst|bg_|payroll|po_budget|advance)/i.test(k) },
  { key: 'procurement', label: 'Procurement & Stores',
    match: (k) => /(mpr|po_|grn|vendor)/i.test(k) },
  { key: 'project', label: 'Project & Site',
    match: (k) => /(dpr|hindrance|ncr|safety|project|task_assigned|schedule_risk|wbs|tender)/i.test(k) },
  { key: 'sales', label: 'Sales & CRM',
    match: (k) => /(opportunity|deal|client)/i.test(k) },
  { key: 'system', label: 'System & Compliance',
    match: (k) => /(compliance|contract|kiosk|absentee|face_)/i.test(k) },
];

const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());

function Chip({ value, onRemove, tone = 'slate' }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] border
      ${tone === 'red' ? 'bg-rose-50 border-rose-200 text-rose-800' :
        'bg-slate-100 border-slate-200 text-slate-700'}`}>
      {value}
      <button onClick={onRemove} className="hover:text-rose-700" aria-label={`Remove ${value}`}>
        <X size={11} />
      </button>
    </span>
  );
}

function EmailChips({ value, onChange, testid, tone, placeholder }) {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const e = draft.trim().toLowerCase();
    if (!e) { setDraft(''); return; }
    if (!validEmail(e)) { toast.error(`Not a valid email: ${e}`); return; }
    if (value.includes(e)) { setDraft(''); return; }
    onChange([...value, e]);
    setDraft('');
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {value.map((e) => (
        <Chip key={e} value={e} tone={tone} onRemove={() => onChange(value.filter((x) => x !== e))} />
      ))}
      <input
        value={draft}
        onChange={(ev) => setDraft(ev.target.value)}
        onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ',') { ev.preventDefault(); commit(); } }}
        onBlur={commit}
        placeholder={placeholder || 'add@email.com'}
        className="text-[11px] px-2 py-1 border border-dashed border-slate-300 rounded-sm focus:border-[#1F7A6E] outline-none min-w-[140px]"
        data-testid={testid}
      />
    </div>
  );
}

function SubscriptionRow({ row, onChange, onSave, onTest, saving }) {
  const [busy, setBusy] = useState(false);
  const updateField = (k, v) => onChange({ ...row, [k]: v, _dirty: true });

  const handleSave = async () => {
    setBusy(true);
    try { await onSave(row); } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try { await onTest(row); } finally { setBusy(false); }
  };

  return (
    <div className="border border-slate-200 rounded-sm p-4 bg-white"
         data-testid={`sub-row-${row.event_type}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2">
            <span className="font-display font-bold text-sm text-[#0F172A]">{row.label || row.event_type}</span>
            {!row.enabled && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-rose-50 border border-rose-200 text-rose-800">
                <ShieldOff size={10} /> Disabled
              </span>
            )}
            {row.is_scheduled && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-amber-50 border border-amber-200 text-amber-800">
                <Clock size={10} /> Scheduled
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{row.description}</p>
          <p className="text-[10px] text-slate-400 font-mono mt-1">{row.event_type}</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
            <Power size={12} className={row.enabled ? 'text-emerald-600' : 'text-slate-400'} />
            <input type="checkbox" checked={!!row.enabled}
                   onChange={(e) => updateField('enabled', e.target.checked)}
                   className="w-4 h-4 accent-[#1F7A6E]"
                   data-testid={`sub-enabled-${row.event_type}`} />
            Enabled
          </label>
          {row.is_scheduled && (
            <input type="time" value={row.send_time_ist || ''}
                   onChange={(e) => updateField('send_time_ist', e.target.value)}
                   className="px-2 py-1 border border-slate-200 rounded-sm text-xs"
                   data-testid={`sub-send-time-${row.event_type}`} />
          )}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-wider text-emerald-700 mb-1">
            Additional emails (bypass opt-in)
          </label>
          <EmailChips value={row.additional_emails || []}
                      onChange={(v) => updateField('additional_emails', v)}
                      testid={`sub-add-${row.event_type}`} />
        </div>
        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-wider text-rose-700 mb-1">
            Excluded emails (force-remove)
          </label>
          <EmailChips value={row.excluded_emails || []}
                      onChange={(v) => updateField('excluded_emails', v)}
                      testid={`sub-excl-${row.event_type}`} tone="red" />
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between text-[10px] text-slate-400">
        <span>{row.updated_by ? `Last edited by ${row.updated_by} · ${(row.updated_at || '').slice(0, 16)}` : 'Not edited yet'}</span>
        <div className="flex gap-2">
          <button onClick={test} disabled={busy}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded-sm text-xs hover:border-[#1F7A6E] disabled:opacity-40"
                  data-testid={`sub-test-${row.event_type}`}>
            <Send size={11} /> Test Send
          </button>
          <button onClick={handleSave} disabled={busy || saving || !row._dirty}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-xs disabled:opacity-40"
                  data-testid={`sub-save-${row.event_type}`}>
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function NotificationSubscriptions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await apiGet('/settings/notification-subscriptions');
      setItems(d.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const updateRow = (idx, next) => {
    setItems((prev) => prev.map((r, i) => (i === idx ? next : r)));
  };

  const save = async (row) => {
    try {
      await apiPut(`/settings/notification-subscriptions/${row.event_type}`, {
        enabled: row.enabled,
        additional_emails: row.additional_emails || [],
        excluded_emails: row.excluded_emails || [],
        send_time_ist: row.send_time_ist || null,
      });
      toast.success(`Saved ${row.label}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const testSend = async (row) => {
    try {
      const r = await apiPost(`/settings/notification-subscriptions/${row.event_type}/test-send`, {});
      toast.success(`Test sent to ${(r.sent_to || []).join(', ') || 'no recipients'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test send failed'); }
  };

  const grouped = useMemo(() => {
    const out = CATEGORIES.map((c) => ({ ...c, rows: [] }));
    const fallback = { key: 'other', label: 'Other', rows: [] };
    items.forEach((row, i) => {
      const cat = CATEGORIES.find((c) => c.match(row.event_type));
      const target = cat ? out.find((g) => g.key === cat.key) : fallback;
      target.rows.push({ ...row, _idx: i });
    });
    if (fallback.rows.length) out.push(fallback);
    return out.filter((g) => g.rows.length);
  }, [items]);

  return (
    <div className="p-6 space-y-5" data-testid="notif-subs-page">
      <PageHeader
        title="Notification Subscriptions"
        subtitle="Per-event overrides for notification recipients — add or exclude emails without changing HR records. Use the master kill-switch to silence an event."
        icon={Bell}
      />
      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>
          <b>Additional emails</b> bypass the per-user opt-in check. <b>Excluded emails</b> are forcibly removed even if they opted-in.
          For scheduled digests, changing the send time takes effect on the next backend restart.
        </span>
      </div>
      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        grouped.map((g) => (
          <Card key={g.key} data-testid={`sub-group-${g.key}`}>
            <CardContent className="pt-5 space-y-3">
              <div className="text-xs font-display font-bold uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
                <Mail size={13} /> {g.label}
                <span className="text-slate-400 font-normal">· {g.rows.length}</span>
              </div>
              {g.rows.map((row) => (
                <SubscriptionRow key={row.event_type} row={row}
                                 onChange={(next) => updateRow(row._idx, next)}
                                 onSave={save} onTest={testSend} />
              ))}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
